import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { Spinner } from '@/components/Spinner';
import type { Role } from '@/types/api';

const PAGE_SIZE = 20; // keep in sync with adminMock PAGE_SIZE

const ROLE_LABEL: Record<Role, string> = { 0: 'User', 1: 'Manager', 2: 'Admin' };

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', { filter, page }],
    queryFn: () => api.listAdminUsers({ filter, page }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => api.removeAdminUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onRemove = (id: number, username: string) => {
    if (window.confirm(`Remove user "${username}"? This cannot be undone. (mock)`)) removeMutation.mutate(id);
  };

  return (
    <>
      <div className="ast-admin-bar">
        <div className="ast-filter-field ast-filter-grow">
          <div className="ast-input-icon">
            <i className="bi bi-funnel" />
            <input className="form-control form-control-sm" placeholder="Filter by username or email…"
              value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }} />
          </div>
        </div>
        <button className="btn btn-ast-blue" onClick={() => navigate('/admin/users/new')}>
          <i className="bi bi-plus-lg me-1" />New User
        </button>
      </div>

      <div className="ast-card">
        <table className="table table-hover align-middle ast-table mb-0">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="text-center py-4"><Spinner /></td></tr>}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={4} className="text-center text-muted py-4">No users found.</td></tr>
            )}
            {!isLoading && rows.map((u) => (
              <tr key={u.id}>
                <td>
                  <button className="ast-link" onClick={() => navigate(`/admin/users/${u.id}/edit`)}>{u.username}</button>
                </td>
                <td>{u.email}</td>
                <td>
                  <span className={`badge ${u.role === 2 ? 'text-bg-primary' : u.role === 1 ? 'text-bg-secondary' : 'text-bg-light text-dark border'}`}>
                    {ROLE_LABEL[u.role]}
                  </span>
                </td>
                <td className="text-end ast-actions">
                  <button className="btn btn-sm btn-outline-secondary" title="Edit"
                    onClick={() => navigate(`/admin/users/${u.id}/edit`)}>
                    <i className="bi bi-pencil" />
                  </button>
                  <button className="btn btn-sm btn-outline-danger" title="Remove"
                    onClick={() => onRemove(u.id, u.username)}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ast-pager">
        <button className="btn btn-sm btn-outline-secondary" disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))} title="Previous page">
          <i className="bi bi-chevron-left" />
        </button>
        <span className="ast-pager-info">Page {page + 1} of {pageCount} · {total} users</span>
        <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} title="Next page">
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    </>
  );
}
